import { Button } from "./ui/button";
import { PageLink } from "./PageLink";
import { Mark } from "./Mark";
import { PageHero, HeroLink } from "./PageHero";
import { AgentPipeline, SquadBoard, CitedAnswer, ConnectorWall, PackList, ChatbotWidget, GovernancePanel, ModelScoreboard, RailsStack } from "./ProductVisuals";
import type { ReactNode } from "react";
import { siteConfig } from "../config/siteConfig";
import type { PlatformTopic } from "../data/platform";
import { ArrowRight, CheckCircle, ArrowUpRight } from "lucide-react";
import { FAQ } from "./FAQ";

interface Props {
  data: PlatformTopic;
  onNavigate: (page: string) => void;
  onNavigateToDemo: () => void;
}

// Per-topic accent so each sub-page reads distinctly: violet for the agentic topics,
// brand blue for the infrastructure ones. Everything else stays on the shared ink system.
const accents = {
  brand: {
    eyebrow: "text-indigo-tint-20",
    dot: "text-royal-indigo",
    chip: "bg-indigo-tint-20 text-royal-indigo",
    softText: "text-royal-indigo",
    band: "bg-royal-indigo",
    check: "text-royal-indigo",
  },
  accent: {
    eyebrow: "text-indigo-tint-20",
    dot: "text-royal-indigo",
    chip: "bg-indigo-tint-20 text-royal-indigo",
    softText: "text-royal-indigo",
    band: "bg-royal-indigo",
    check: "text-royal-indigo",
  },
} as const;

const topicVisual: Record<string, ReactNode> = {
  why: <RailsStack />,
  agents: <AgentPipeline />,
  squads: <SquadBoard />,
  rag: <CitedAnswer />,
  chatbots: <ChatbotWidget />,
  governance: <GovernancePanel />,
  "edge-ai": <ModelScoreboard />,
  integrations: <ConnectorWall />,
  "domain-packs": <PackList />,
};

export function PlatformTopicPage({ data, onNavigate, onNavigateToDemo }: Props) {
  const a = accents[data.accent];

  return (
    <div className="min-h-screen bg-white">
      <PageHero
        crumb={
          <>
            <PageLink page={"platform"} onNavigate={onNavigate} className="font-semibold text-royal-indigo hover:text-dark-indigo">Platform</PageLink>
            <span className="mx-1.5">/</span>
            {data.eyebrow}
          </>
        }
        title={data.headline}
        lede={data.subhead}
        actions={
          <>
            <Button variant="cta" size="cta" onClick={onNavigateToDemo}>Schedule a demo</Button>
            <HeroLink onClick={() => (window.location.href = siteConfig.urls.developers)}>Developer docs</HeroLink>
          </>
        }
        visual={topicVisual[data.slug]}
      />

      {/* Intro lead */}
      <section className="py-16 lg:py-20 bg-white border-b border-softblack-tint-20">
        <div className="container mx-auto px-4">
          <p className="max-w-3xl mx-auto text-left md:text-center text-[20px] md:text-[24px] lg:text-[28px] leading-snug font-medium text-soft-black">
            {data.intro}
          </p>
        </div>
      </section>

      {/* Feature blocks — alternating bands + glass point cards */}
      {data.blocks.map((block, i) => {
        const dark = i % 2 === 1;
        return (
          <section key={block.title} className={dark ? "py-16 lg:py-20 bg-dark-indigo text-white" : "py-16 lg:py-20 band-surface"}>
            <div className="container mx-auto px-4">
              <div className={`grid lg:grid-cols-2 gap-10 lg:gap-14 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div>
                  <span className={`text-sm font-semibold uppercase tracking-wider ${dark ? a.eyebrow : a.softText}`}>
                    0{i + 1}
                  </span>
                  <h2 className={`mt-3 text-[26px] lg:text-[34px] leading-tight font-bold tracking-tight ${dark ? "text-white" : "text-dark-indigo"}`}>
                    {block.title}
                  </h2>
                  <p className={`mt-4 text-[19px] leading-relaxed ${dark ? "text-indigo-tint-20" : "text-soft-black"}`}>{block.body}</p>
                </div>
                {block.points && (
                  <div
                    className={
                      dark
                        ? "rounded-2xl bg-transparent border border-royal-indigo  p-6 lg:p-8"
                        : "rounded-2xl bg-white border border-softblack-tint-20  p-6 lg:p-8 "
                    }
                  >
                    <ul className="space-y-4">
                      {block.points.map((p) => (
                        <li key={p} className={`flex items-start gap-3 ${dark ? "text-indigo-tint-20" : "text-soft-black"}`}>
                          <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${dark ? a.eyebrow : a.check}`} />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </section>
        );
      })}

      {/* Card groups — reasons, patterns, what's inside */}
      {data.cardGroups?.map((g, gi) => (
        <section key={g.headline} className={`py-20 lg:py-24 ${gi % 2 === 0 ? "bg-white" : "band-surface"}`}>
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mb-12">
              <span className="text-sm font-semibold uppercase tracking-wider text-royal-indigo">{g.eyebrow}</span>
              <h2 className="mt-3 text-[28px] lg:text-[36px] leading-tight font-bold tracking-tight text-dark-indigo">{g.headline}</h2>
              {g.sub && <p className="mt-4 text-[19px] text-soft-black">{g.sub}</p>}
            </div>
            <div className={`grid gap-5 sm:grid-cols-2 ${g.items.length % 3 === 0 ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}>
              {g.items.map((it) => (
                <div key={it.title} className="rounded-2xl bg-white border border-softblack-tint-20 p-6">
                  <h3 className="text-[19px] font-semibold text-dark-indigo mb-2">{it.title}</h3>
                  <p className="text-[15px] leading-relaxed text-soft-black">{it.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* How it works — connected steps on a contrasting band */}
      {data.how && (
        <section className={`py-20 lg:py-24 ${a.band} text-white`}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-14 max-w-3xl mx-auto">
              <h2 className="text-[30px] lg:text-[38px] leading-tight font-bold tracking-tight">
                {data.howHeadline}
              </h2>
            </div>
            <div className={`grid sm:grid-cols-2 gap-5 max-w-6xl mx-auto ${data.how.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}>
              {data.how.map((step, i) => (
                <div key={step.title} className="relative rounded-2xl bg-dark-indigo border border-indigo-tint-50 p-6">
                  <div className="w-10 h-10 rounded-xl bg-white text-dark-indigo font-display flex items-center justify-center font-bold mb-4">
                    {i + 1}
                  </div>
                  <h3 className="text-[19px] font-semibold mb-1">{step.title}</h3>
                  <p className="text-sm text-indigo-tint-20 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Capabilities chips */}
      {data.capabilities && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex flex-wrap justify-center gap-3">
              {data.capabilities.map((c) => (
                <span key={c} className={`px-4 py-2 rounded-full text-sm font-medium ${a.chip}`}>
                  {c}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Connector catalogue (Integrations) */}
      {data.connectorGroups && (
        <section className="py-20 lg:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mb-12">
              <span className="text-sm font-semibold uppercase tracking-wider text-royal-indigo">Connectors</span>
              <h2 className="mt-3 text-[28px] lg:text-[36px] leading-tight font-bold tracking-tight text-dark-indigo">What TurfAI <Mark>connects to</Mark> today.</h2>
              <p className="mt-4 text-[19px] text-soft-black">What's connected today. We add to it with each deployment.</p>
            </div>
            <div className="space-y-10">
              {data.connectorGroups.map((g) => (
                <div key={g.tier}>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-soft-black mb-4">{g.tier}</h3>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {g.items.map((c) => (
                      <div key={c.name} className="rounded-lg border border-softblack-tint-20 p-4">
                        <div className="font-semibold text-dark-indigo">{c.name}</div>
                        <p className="mt-1 text-sm leading-relaxed text-soft-black">{c.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-10 text-[17px] text-soft-black">
              Don't see yours? If it has an API, we connect it via REST or MCP.{" "}
              <button onClick={onNavigateToDemo} className="font-semibold text-royal-indigo hover:text-dark-indigo">Ask about a connector</button>
            </p>
          </div>
        </section>
      )}

      {/* Supported models (Edge AI) */}
      {data.models && (
        <section className="py-20 lg:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mb-10">
              <h2 className="text-[28px] lg:text-[36px] leading-tight font-bold tracking-tight text-dark-indigo">Popular small models, <Mark>all under 36B</Mark>.</h2>
              <p className="mt-4 text-[19px] text-soft-black">Any model Ollama or vLLM can serve runs in a TurfAI workflow. These are the families teams ask for most.</p>
            </div>
            <div className="overflow-x-auto rounded-lg border border-softblack-tint-20">
              <table className="w-full min-w-[640px] text-left text-[15px]">
                <thead className="bg-indigo-tint-05 text-dark-indigo">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-semibold">Model family</th>
                    <th scope="col" className="px-4 py-3 font-semibold">Sizes</th>
                    <th scope="col" className="px-4 py-3 font-semibold">Type</th>
                    <th scope="col" className="px-4 py-3 font-semibold">Benchmarked in our lab</th>
                  </tr>
                </thead>
                <tbody>
                  {data.models.map((m) => (
                    <tr key={m.family} className="border-t border-softblack-tint-20">
                      <th scope="row" className="px-4 py-3 font-semibold text-dark-indigo">{m.family}</th>
                      <td className="px-4 py-3 text-soft-black">{m.sizes}</td>
                      <td className="px-4 py-3 text-soft-black">{m.kind}</td>
                      <td className="px-4 py-3 text-soft-black">{m.tested}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Packs (Domain Packs) */}
      {data.packs && (
        <section className="py-20 lg:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mb-12">
              <span className="text-sm font-semibold uppercase tracking-wider text-royal-indigo">The packs</span>
              <h2 className="mt-3 text-[28px] lg:text-[36px] leading-tight font-bold tracking-tight text-dark-indigo">
                Four <Mark>live</Mark> today. Two in design.
              </h2>
              <p className="mt-4 text-[19px] text-soft-black">Each pack is a real deployment pattern. Pick the one closest to your problem.</p>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data.packs.map((pk) => {
                const live = pk.status === "Live";
                const body = (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-[19px] text-dark-indigo">{pk.name} Domain Pack</span>
                      <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${live ? "text-success-text" : "text-soft-black"}`}>
                        <span className={`h-2 w-2 rounded-full ${live ? "bg-success-text" : "bg-indigo-tint-50"}`} aria-hidden="true" />
                        {pk.status}
                      </span>
                    </div>
                    <p className="text-[15px] leading-relaxed text-soft-black">{pk.desc}</p>
                    {pk.page && (
                      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-royal-indigo">
                        Explore {pk.name} <ArrowRight className="w-4 h-4" />
                      </span>
                    )}
                  </>
                );
                return pk.page ? (
                  <PageLink page={pk.page!} onNavigate={onNavigate} key={pk.name} className="text-left rounded-2xl border border-softblack-tint-20 hover:border-royal-indigo transition-colors p-6">
                    {body}
                  </PageLink>
                ) : (
                  <div key={pk.name} className="rounded-2xl border border-dashed border-indigo-tint-50 p-6">{body}</div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Outcomes */}
      {data.outcomes && (
        <section className="py-20 lg:py-24 bg-white border-y border-softblack-tint-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mb-12">
              <span className="text-sm font-semibold uppercase tracking-wider text-royal-indigo">Production outcomes, not pilot promises</span>
              <h2 className="mt-3 text-[28px] lg:text-[36px] leading-tight font-bold tracking-tight text-dark-indigo">The outcomes are already live.</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {data.outcomes.map((o) => (
                <div key={o.label} className="border-l-4 border-vibrant-orange pl-4">
                  <div className="font-display text-[44px] font-bold tracking-tight text-dark-indigo leading-none">{o.value}</div>
                  <div className="mt-2 font-semibold text-soft-black">{o.label}</div>
                  <div className="text-sm text-soft-black">{o.note}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Comparisons */}
      {data.comparisons && (
        <section className="py-20 lg:py-24 band-surface">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mb-12">
              <span className="text-sm font-semibold uppercase tracking-wider text-royal-indigo">How TurfAI compares</span>
              <h2 className="mt-3 text-[28px] lg:text-[36px] leading-tight font-bold tracking-tight text-dark-indigo">The work the alternatives leave to you.</h2>
            </div>
            <div className="grid gap-5 lg:grid-cols-3">
              {data.comparisons.map((c) => (
                <div key={c.vs} className="rounded-2xl bg-white border border-softblack-tint-20 p-6 flex flex-col">
                  <div className="text-sm font-semibold text-royal-indigo">TurfAI vs</div>
                  <h3 className="text-[22px] font-semibold text-dark-indigo mb-3">{c.vs}</h3>
                  <p className="text-[15px] leading-relaxed text-soft-black flex-1">{c.body}</p>
                  <p className="mt-5 pt-4 border-t border-softblack-tint-20 font-semibold text-dark-indigo">{c.tag}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {data.faq && (
        <FAQ
          title={`${data.name} FAQ`}
          subtitle="Questions teams ask before they switch."
          items={data.faq.map((f) => ({ question: f.q, answer: f.a }))}
        />
      )}

      {/* Related links — cards */}
      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-center text-[26px] lg:text-[34px] font-bold tracking-tight text-dark-indigo mb-12">
            Keep <Mark>exploring</Mark>
          </h2>
          <div className={`grid gap-5 max-w-5xl mx-auto ${data.related.length >= 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`}>
            {data.related.map((r) => (
              <PageLink page={r.page} onNavigate={onNavigate}
                key={r.page}
                className="group text-left rounded-2xl bg-white border border-softblack-tint-20 hover:border-indigo-tint-50 transition-all p-6"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-dark-indigo group-hover:text-royal-indigo transition-colors">{r.label}</span>
                  <ArrowUpRight className="w-4 h-4 text-indigo-tint-20 group-hover:text-royal-indigo transition-colors" />
                </div>
                <p className="text-sm text-soft-black leading-relaxed">{r.desc}</p>
              </PageLink>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-dark-indigo text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-[30px] lg:text-[40px] leading-tight font-bold tracking-tight mb-6">
            See {data.name} <Mark>in action</Mark>
          </h2>
          <p className="text-[22px] text-indigo-tint-20 mb-8 max-w-2xl mx-auto">
            Talk to us about your workflow, or explore the docs to build on TurfAI today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="onDark" size="lg" onClick={onNavigateToDemo}>
              Schedule a demo
            </Button>
            <Button variant="outlineOnDark" size="lg" onClick={() => (window.location.href = siteConfig.urls.developers)}
            >
              Developer docs <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
